import { ChevronDown, LogOut, Settings, Shield } from "lucide-react";
import { useNavigate } from "react-router";
import { UserAvatar } from "@/components/nav/user-avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth-context";
import { cn } from "@/lib/cn";

export function AccountMenu({
  className,
  align = "end",
  side = "bottom",
}: {
  className?: string;
  align?: "start" | "center" | "end";
  side?: "top" | "right" | "bottom" | "left";
}) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const label = user?.displayName?.trim() || user?.email || "Account";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          size="sm"
          className={cn("max-w-[14rem] gap-2 pl-1.5", className)}
          aria-label="Account Menu"
        >
          <UserAvatar
            displayName={user?.displayName}
            email={user?.email}
            avatarUrl={user?.avatarUrl}
            size="sm"
          />
          <span className="min-w-0 flex-1 truncate text-left">{label}</span>
          <ChevronDown
            className="h-3.5 w-3.5 shrink-0 opacity-70"
            aria-hidden
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} side={side} className="w-56">
        <DropdownMenuLabel className="px-2 py-1.5 font-normal">
          <div className="flex items-center gap-2.5">
            <UserAvatar
              displayName={user?.displayName}
              email={user?.email}
              avatarUrl={user?.avatarUrl}
              size="md"
            />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-rs-ink">
                {user?.displayName || "Account"}
              </p>
              {user?.email ? (
                <p className="truncate text-xs text-rs-ink-muted">
                  {user.email}
                </p>
              ) : null}
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            navigate(`/settings/profile`);
          }}
        >
          <Settings className="h-3.5 w-3.5" aria-hidden />
          Settings
        </DropdownMenuItem>
        {user?.isPlatformAdmin ? (
          <DropdownMenuItem
            onSelect={() => {
              navigate("/superadmin");
            }}
          >
            <Shield className="h-3.5 w-3.5" aria-hidden />
            Super Admin
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          destructive
          onSelect={() => {
            void signOut().then(() => navigate("/login"));
          }}
        >
          <LogOut className="h-3.5 w-3.5" aria-hidden />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
